import styled from "@emotion/styled";
import { uniqueId } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import * as React from "react";
// FIXME: Fix this the next time the file is edited.
// @ts-expect-error TS(6133): 'useSelector' is declared but its value is never r... Remove this comment to see the full error message
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { connect, useDispatch, useSelector } from "react-redux";
// @ts-expect-error TS(7016)
import RecordRTC from "recordrtc";
import { Editor, Transforms } from "slate";
import { ReactEditor, useSlate } from "slate-react";

import { Button, ModalPortal } from "@livechat/design-system-react-components";

import { ButtonGroup } from "components/button/ButtonGroup";
import LinkButton from "components/button/LinkButton";
import { AdaptedModal } from "components/design-system-adapters/AdaptedModal";
import { IconsEnum } from "components/icon";
import ShortcutTooltip from "components/shortcut-tooltip/ShortcutTooltip";
import { ToolbarIcon } from "components/toolbar";
import { IToolbarIconProps } from "components/toolbar/icon";
import { ToastContent, getToastContent } from "constants/toasts";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isInIframe } from "helpers/app";
import { isMac } from "helpers/isMac";
import useModal from "hooks/use-modal";
import { IStoreState } from "interfaces/storeState";
import { ApiManager } from "services/api";
import { getItem, removeItem, saveItem } from "services/session-storage";
import { selectIsViewer } from "store/entities/agents";
import { selectTextEnhancements } from "store/entities/license";
import { ToastAutoHideDelay, ToastVariant, ToastsActions } from "store/features/toasts";
import { StackingContextLevel } from "styles/stacking-context-level";

import { AtIcon, StyledButtonWrapper, StyledIconsWrapper, StyledMainToolbarContainer } from "./styles";
import AiTextEnhancementsToolbar from "./toolbar/AiTextEnhancementsToolbar";
import TextFormattingToolbar from "./toolbar/TextFormattingToolbar";

export interface IToolbarProps {
  disabled: boolean;
  attachmentsEnabled: boolean;
  mobile: boolean;
  privateMode?: boolean;
  leftNodes?: React.ReactNode;
  rightNodes?: React.ReactNode;
  sendButtonLabel?: string;
  sendButtonDisabled?: boolean;
  sendButtonLoading?: boolean;
  onCannedResponseClick(): void;
  onAttachmentClick(): void;
  onMentionClick?(): void;
  onLinkInsert: React.EventHandler<React.MouseEvent<HTMLDivElement>>;
  textFormattingVisible: boolean;
  onTextFormattingClick: React.EventHandler<React.MouseEvent<HTMLDivElement>>;
  aiTextEnhancementsVisible: boolean;
  onAiFormattingClick: React.EventHandler<React.MouseEvent<HTMLDivElement>>;
  onSendButtonClick(e: React.MouseEvent<HTMLButtonElement>): void;
  onSendButtonMouseDown(e: React.MouseEvent<HTMLButtonElement>): void;
}

export interface IStateProps {
  textEnhancements: boolean;
  isViewer: boolean;
}

export const ModalBlock = styled.div`
  font-size: 15px;
  padding: 20px;
  line-height: 1.34;

  max-width: 560px;

  @media (max-width: 400px) {
    font-size: 14px;
    padding: 10px;
  }

  p {
    margin-bottom: 16px;
  }
`;

const UpgradePlanModal: React.FC<{ onClose(): void }> = ({ onClose }) => (
  <ModalPortal zIndex={StackingContextLevel.Modal}>
    <AdaptedModal
      onClose={onClose}
      heading="Plan upgrade required"
      footer={
        <ButtonGroup>
          <Button kind="secondary" onClick={onClose} size="large">
            Close
          </Button>
          <LinkButton to="/subscription" kind="primary" size="large">
            Upgrade
          </LinkButton>
        </ButtonGroup>
      }
    >
      <ModalBlock>
        <p>
          This feature is only available for licenses on the <strong>Team</strong> plan or higher.
        </p>
      </ModalBlock>
    </AdaptedModal>
  </ModalPortal>
);

const SystemPermissionsModal: React.FC<{ onClose(): void }> = ({ onClose }) => (
  <ModalPortal zIndex={StackingContextLevel.Modal}>
    <AdaptedModal
      onClose={onClose}
      heading="Can't share your screen"
      footer={
        <ButtonGroup>
          <Button kind="primary" onClick={onClose} size="large">
            Got it
          </Button>
        </ButtonGroup>
      }
    >
      <ModalBlock>
        <p>
          Your browser may not have permission to use screen recording on your computer. To grant permission, go to
          System Preferences.
        </p>
      </ModalBlock>
    </AdaptedModal>
  </ModalPortal>
);

const SCREEN_RECORDING_BUT_TRANSITIONED_KEY = "SCREEN_RECORDING_BUT_TRANSITIONED";

const ScreenRecordingIcon: React.FC<Omit<IToolbarIconProps, "onMouseDown">> = props => {
  const dispatch = useDispatch();
  const editor = useSlate();
  const [recordingStarted, setRecordingStarted] = useState(false);
  const currentRecorder = useRef(null);
  const [upgradePlanModalVisible, { show: showUpgradePlanModal, close: closeUpgradePlanModal }] = useModal(false);
  const [
    systemPermissionsDeniedModalVisible,
    { show: showPermissionsDeniedModal, close: closePermissionsDeniedModal },
  ] = useModal(false);
  useEffect(
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    () => () => {
      if (recordingStarted) {
        saveItem(SCREEN_RECORDING_BUT_TRANSITIONED_KEY, true);
      }
    },
    [recordingStarted]
  );

  const handleReadyVideo = useCallback(async () => {
    const hasTransitioned = getItem(SCREEN_RECORDING_BUT_TRANSITIONED_KEY);
    // @ts-expect-error TS(2531): Object is possibly 'null'.
    const videoBlob = currentRecorder.current.getBlob();

    if (!videoBlob) {
      dispatch(
        ToastsActions.createToast({
          variant: ToastVariant.Error,
          content: getToastContent(ToastContent.SCREEN_RECORDING_FAILURE),
        })
      );
      return;
    }
    currentRecorder.current = null;

    const videoFile = new File([videoBlob], `hd-recording-${new Date().toISOString().replace(/:|\./g, "-")}.webm`);
    const formData = new FormData();
    formData.append("video-blob", videoFile);

    const toastID = uniqueId();
    dispatch(
      ToastsActions.createToast({
        variant: ToastVariant.Success,
        content: getToastContent(ToastContent.SCREEN_RECORDING_UPLOAD_IN_PROGRESS),
        id: toastID,
        removable: false,
        autoHideDelayTime: ToastAutoHideDelay.Infinite,
      })
    );

    let response;
    try {
      response = await ApiManager.videoApi.uploadScreencast(formData);

      if (response.error) {
        dispatch(
          ToastsActions.createToast({
            variant: ToastVariant.Error,
            content: getToastContent(ToastContent.SCREEN_RECORDING_FAILURE),
          })
        );
        trackEngagement("Screen recording: Recording failed", EventPlace.Tickets);
        // @ts-expect-error TS(18046)
      } else if (response.result.result === "ok") {
        const shouldCopyToClipboard = hasTransitioned;
        // @ts-expect-error TS(2339)
        const { url } = response.result;
        let clipboardProblem = false;

        if (shouldCopyToClipboard) {
          try {
            await navigator.clipboard.writeText(url);
          } catch {
            clipboardProblem = true;
          }
        } else {
          if (!ReactEditor.isFocused(editor)) {
            ReactEditor.focus(editor);
            Transforms.select(editor, Editor.start(editor, []));
          }

          Transforms.insertFragment(editor, [
            {
              type: "paragraph",
              children: [{ type: "link", url, children: [{ text: "Click here to see screen recording" }] }],
            },
          ]);
        }

        trackEngagement("Screen recording: Recording successful", EventPlace.Tickets, {
          recordingSize: videoFile.size,
        });

        dispatch(
          ToastsActions.createToast({
            content: getToastContent(ToastContent.SCREEN_RECORDING_UPLOAD_SUCCESS, {
              transitioned: hasTransitioned,
              showUrl: clipboardProblem ? url : undefined,
            }),
            ...(clipboardProblem || hasTransitioned ? { autoHideDelayTime: ToastAutoHideDelay.Infinite } : {}),
            variant: ToastVariant.Success,
          })
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      dispatch(ToastsActions.removeToast({ id: toastID }));
      removeItem(SCREEN_RECORDING_BUT_TRANSITIONED_KEY);
      if (!hasTransitioned) {
        setRecordingStarted(false);
      }
    }
  }, [dispatch, editor]);

  const SCREEN_RECORDING_TIMEOUT = 10 * 60 * 1000;

  const handleScreenRecording = useCallback(
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (event: any) => {
      event.preventDefault();

      if (currentRecorder.current) {
        // @ts-expect-error TS(2339): Property 'stream' does not exist on type 'never'.
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        currentRecorder.current.stream.getTracks().forEach((track: any) => {
          if (track.readyState !== "ended") {
            track.stop();
          }
          // @ts-expect-error TS(2531): Object is possibly 'null'.
          currentRecorder.current.stopRecording(handleReadyVideo);
        });
      }

      if (!props.disabled && !recordingStarted) {
        removeItem(SCREEN_RECORDING_BUT_TRANSITIONED_KEY);
        let mediaStream: MediaStream;
        try {
          mediaStream = await navigator.mediaDevices.getDisplayMedia({
            audio: false,
            video: { frameRate: { ideal: 30.0 } },
          });
        } catch (error) {
          // @ts-expect-error TS(2571): Object is of type 'unknown'.
          if (/(?:Permission denied|not allowed|can not be found)/.test(error.message)) {
            showPermissionsDeniedModal(true);
          }

          setRecordingStarted(false);
          removeItem(SCREEN_RECORDING_BUT_TRANSITIONED_KEY);
          return;
        }

        setRecordingStarted(true);
        trackEngagement("Screen recording: Recording started", EventPlace.Tickets);
        const recorderType = RecordRTC.MediaStreamRecorder;

        const recorder = RecordRTC(mediaStream, { type: "video", recorderType });
        recorder.stream = mediaStream;
        currentRecorder.current = recorder;
        recorder.startRecording();

        mediaStream.getTracks().forEach(track => {
          track.addEventListener("ended", () => {
            recorder.stopRecording(handleReadyVideo);
          });
          track.addEventListener("mute", () => {
            recorder.pauseRecording();
          });
          track.addEventListener("unmute", () => {
            recorder.resumeRecording();
          });
        });

        setTimeout(() => {
          if (recorder.getState() === "recording") {
            mediaStream.getTracks().forEach(track => {
              track.stop();
            });
            recorder.stopRecording(handleReadyVideo);
          }
        }, SCREEN_RECORDING_TIMEOUT);
      }
    },
    [
      SCREEN_RECORDING_TIMEOUT,
      handleReadyVideo,
      props.disabled,
      recordingStarted,
      showPermissionsDeniedModal,
      showUpgradePlanModal,
    ]
  );

  let tooltip = "Screen recording";
  if (recordingStarted) {
    tooltip = "Stop recording";
  }

  const isTablet =
    /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/i.test(
      navigator.userAgent
    );
  const mediaRecorderAvailable =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (window as Window & { MediaRecorder?: any }).MediaRecorder !== "undefined" &&
    !props.mobile &&
    !isTablet &&
    !isInIframe();

  return !mediaRecorderAvailable ? null : (
    <>
      <ToolbarIcon
        {...props}
        disabled={props.disabled}
        onMouseDown={handleScreenRecording}
        tooltip={tooltip}
        active={recordingStarted}
      />
      {upgradePlanModalVisible && <UpgradePlanModal onClose={closeUpgradePlanModal} />}
      {systemPermissionsDeniedModalVisible && <SystemPermissionsModal onClose={closePermissionsDeniedModal} />}
    </>
  );
};

class ToolbarComponent extends React.PureComponent<IToolbarProps & IStateProps> {
  static defaultProps = {
    privateMode: false,
    leftNodes: null,
    rightNodes: null,
    sendButtonLabel: "Send",
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  handleCannedResponseClick = (event: any) => {
    event.preventDefault();

    if (!this.props.disabled) {
      this.props.onCannedResponseClick();
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  handleMentionClick = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();

    if (!this.props.disabled) {
      // @ts-expect-error TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
      this.props.onMentionClick();
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleAttachmentClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!this.props.disabled) {
      this.props.onAttachmentClick();
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleSendButtonMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!this.props.disabled) {
      this.props.onSendButtonMouseDown(e);
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleSendButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!this.props.disabled) {
      this.props.onSendButtonClick(e);
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleTextEnhancementsMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    const { aiTextEnhancementsVisible, onAiFormattingClick } = this.props;
    if (!aiTextEnhancementsVisible) {
      trackEngagement("AI Text Enhancement: Menu opened");
    }
    onAiFormattingClick(e);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render() {
    const {
      sendButtonLabel,
      disabled,
      sendButtonDisabled,
      sendButtonLoading,
      privateMode,
      leftNodes,
      rightNodes,
      mobile,
      attachmentsEnabled,
      textFormattingVisible,
      onTextFormattingClick,
      aiTextEnhancementsVisible,
      onLinkInsert,
    } = this.props;

    return (
      <div>
        {textFormattingVisible && (
          <TextFormattingToolbar
            disabled={disabled}
            privateMode={!!privateMode}
            onLinkInsert={onLinkInsert}
            mobile={mobile}
          />
        )}
        {aiTextEnhancementsVisible && (
          <AiTextEnhancementsToolbar disabled={disabled} privateMode={!!privateMode} mobile={mobile} />
        )}
        <StyledMainToolbarContainer>
          <StyledIconsWrapper>
            {leftNodes}
            <ToolbarIcon
              testSelector="canned-responses-button"
              icon={IconsEnum.CannedResponse}
              tooltip="Canned response"
              disabled={disabled}
              privateMode={!!privateMode}
              mobile={mobile}
              onMouseDown={this.handleCannedResponseClick}
            />
            {attachmentsEnabled && (
              <ToolbarIcon
                testSelector="attachments-button"
                icon={IconsEnum.Attachment}
                tooltip="Attachment"
                disabled={disabled}
                privateMode={!!privateMode}
                mobile={mobile}
                onMouseDown={this.handleAttachmentClick}
              />
            )}
            <ScreenRecordingIcon
              testSelector="screen-recording"
              icon={IconsEnum.ScreenRecord}
              disabled={disabled}
              privateMode={!!privateMode}
              mobile={mobile}
            />
            <ToolbarIcon
              testSelector="toggle-richtext-bar-button"
              icon={IconsEnum.TextFormat}
              disabled={disabled}
              tooltip="Text formatting"
              privateMode={!!privateMode}
              customSize="22px"
              active={textFormattingVisible}
              mobile={mobile}
              onMouseDown={onTextFormattingClick}
            />

            <ToolbarIcon
              testSelector="toggle-richtext-ai-bar-button"
              icon={IconsEnum.TextEnhancements}
              disabled={disabled}
              privateMode={!!privateMode}
              active={aiTextEnhancementsVisible}
              mobile={mobile}
              tooltip="AI text enhancements"
              onMouseDown={this.handleTextEnhancementsMenu}
            />

            {this.props.onMentionClick && (
              <ToolbarIcon
                testSelector="toggle-mention-button"
                icon={<AtIcon privateMode={!!privateMode}>@</AtIcon>}
                disabled={disabled}
                tooltip={privateMode ? "Mention" : "Mention (available in Private Mode)"}
                mobile={mobile}
                privateMode={!!privateMode}
                onMouseDown={this.handleMentionClick}
              />
            )}
          </StyledIconsWrapper>
          <StyledButtonWrapper
            // FIXME: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onClick={(ev: any) => {
              ev.preventDefault();
              ev.stopPropagation();
            }}
          >
            {rightNodes}
            <ShortcutTooltip
              trigger={
                <Button
                  kind="high-contrast"
                  size="compact"
                  disabled={sendButtonDisabled || disabled}
                  loading={sendButtonLoading}
                  // @ts-expect-error TS(2322)
                  onClick={this.handleSendButtonClick}
                  // @ts-expect-error TS(2322)
                  onMouseDown={this.handleSendButtonMouseDown}
                >
                  {sendButtonLabel}
                </Button>
              }
              title={sendButtonLabel ?? "Submit"}
              firstKey={isMac(navigator.userAgent) ? "⌘" : "ctrl"}
              secondKey="enter"
              hideTooltip={sendButtonDisabled || disabled}
            />
          </StyledButtonWrapper>
        </StyledMainToolbarContainer>
      </div>
    );
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: IStoreState) => ({
  textEnhancements: selectTextEnhancements(state),
  isViewer: selectIsViewer(state),
});

export const Toolbar = connect(mapStateToProps)(ToolbarComponent);
